import { API_URL } from "@/mainconfig"; // Impor instance API_URL dari mainconfig.js
import router from "@/router"; // Pastikan Anda mengimpor instance router
import { getResponseMessage } from "@/utils/responseMapping";
import i18n from "@/plugins/LanguageComponent.js";
import { encrypt, decrypt } from "@/utils/cryptoHelper";
import { SETTINGS } from "@/mainconfig";

const getInitialIsGuest = () => {
    try {
        const encrypted = localStorage.getItem("isGuest");
        if (!encrypted) {
            return true;
        }
        return decrypt(encrypted) === "true";
    } catch (error) {
        console.warn("Failed to parse isGuest from storage, fallback to guest mode.", error);
        return true;
    }
};

const state = {
    token: localStorage.getItem("token") || null, // Ambil token dari localStorage saat pertama kali dimuat
    error: null,
    errorKey: null,
    errorMessage: null, // Tambahkan ini
    loading: false,
    user: (() => {
        const encryptedUser = localStorage.getItem("user");
        if (encryptedUser) {
            try {
                const decryptedUser = decrypt(encryptedUser);
                return JSON.parse(decryptedUser); // Parsing hasil dekripsi
            } catch (error) {
                console.error("Error decrypting or parsing user:", error);
                localStorage.removeItem("user"); // Hapus data bermasalah
                return null;
            }
        }
        return null;
    })(),
    isAuthenticated: !!localStorage.getItem("token"), // Set isAuthenticated berdasarkan token di localStorage
    isGuest: getInitialIsGuest(),
    verificationIssues: [],
    registrationData: {
        name: "",
        emailOrPhoneNumber: "",
        email: "",
        phone: "",
        password: "",
    },
    lastLoginAttempt: null, // Timestamp percobaan login terakhir
    loginAttempts: 0, // Hitungan percobaan login
    fingerprintId: localStorage.getItem("fingerprintId") || null, // Menyimpan fingerprint ID
    websiteLogo: localStorage.getItem("WEBSITE_LOGO") || null,
    monthlyBookingRequireLogin:
        localStorage.getItem("MONTHLY_BOOKING_REQUIRE_LOGIN") === "true",
};

const getters = {
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    isAuthenticated: (state) => state.isAuthenticated,
    isGuest: (state) => state.isGuest,
    verificationIssues: (state) => state.verificationIssues,
    getUser: (state) => state.user,
    getWebsiteLogo: (state) => state.websiteLogo,
    getMonthlyBookingRequireLogin: (state) => state.monthlyBookingRequireLogin,
};

const actions = {
    async sendOTP({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/sendOTP", payload);
            commit("setError", null);
            return response;
        } catch (error) {
            console.log("xxxxxxx1", error);
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred during OTP sending."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async requestLoginOTP({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/requestLoginOTP", payload);
            commit("setError", null);
            return response;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async loginWithOTP({ dispatch, commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/loginWithOTP", payload);
            const token = response.data.data;

            commit("setToken", token);
            commit("setAuthenticated", true);
            commit("setGuest", false);
            commit("setError", null);

            await dispatch("fetchUser");

            return response;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async register({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/register", payload);
            commit("setError", null);
            return response;
        } catch (error) {
            commit("setMonthlyBookingRequireLogin", false);
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred during registration."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async login({ dispatch, commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/login", payload);
            const token = response.data.data;
            commit("setToken", token);
            commit("setAuthenticated", true);
            commit("setGuest", false);
            commit("setError", null);

            await dispatch("fetchUser");

            return response;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError"); // Kirim errorKey ke mutation
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async logout({ dispatch, commit }) {
        const visitorId = localStorage.getItem("fingerprintId");

        // Hapus data auth, set mode guest
        commit("setToken", null);
        commit("setUser", null);
        commit("setAuthenticated", false);
        commit("setGuest", true);

        // Bersihkan storage auth tanpa menghapus fingerprint
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("MONTHLY_BOOKING_REQUIRE_LOGIN");
        if (visitorId) {
            localStorage.setItem("fingerprintId", visitorId);
        }
        sessionStorage.clear();

        // Kirim ulang fingerprint untuk memulai sesi guest
        try {
            if (visitorId) {
                console.log("SENDF", 2);
                await dispatch("sendFingerprint", visitorId); // Trigger sesi guest
            } else {
                console.warn(
                    "Visitor ID is missing. Cannot reinitialize guest session."
                );
            }
        } catch (err) {
            console.error("Error while reinitializing guest session:", err);
        }

        // Redirect ke halaman utama dengan memastikan router diarahkan
        try {
            await router.push({ name: "Home" });
        } catch (err) {
            console.error("Error while redirecting to Home:", err);
        }

        // Refresh halaman untuk memastikan state benar-benar dihapus
        window.location.reload();
    },

    async continueWithSSO({ commit, dispatch }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                "/user/continueWithSSO",
                payload
            );
            const responseData = response?.data?.data;
            const token =
                typeof responseData === "string"
                    ? responseData
                    : responseData?.token || null;

            // Simpan token dan set sebagai authenticated
            if (token) {
                commit("setToken", token);
                commit("setAuthenticated", true);
                commit("setGuest", false);
            }
            commit("setError", null);

            // Fetch user data setelah login dengan SSO
            if (token) {
                await dispatch("fetchUser");
            }

            return response;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async continueWithGoogle({ commit, dispatch }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/continueWithGoogle", payload);
            const responseData = response?.data?.data;
            const token =
                typeof responseData === "string"
                    ? responseData
                    : responseData?.token || null;

            if (token) {
                commit("setToken", token);
                commit("setAuthenticated", true);
                commit("setGuest", false);
            }
            commit("setError", null);

            if (token) {
                await dispatch("fetchUser");
            }

            return response;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async completeGoogleOnboarding({ dispatch, commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                "/user/completeGoogleOnboarding",
                payload
            );
            commit("setError", null);
            await dispatch("fetchUser");
            return response;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async sendFingerprint({ dispatch, commit }, visitorId) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/guest", {
                VisitorId: visitorId,
            });

            const guestToken = response.data.data;
            commit("setToken", guestToken);
            commit("setError", null);

            console.log("response_guest", response);

            await dispatch("fetchUser");
            await dispatch("getWebsiteSetting");

            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred while sending fingerprint."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async fetchUser({ dispatch, commit, state }) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get("/user/me", {
                headers: {
                    Authorization: `Bearer ${state.token}`,
                },
            });
            const isGuest = response.data?.data?.IsGuest;

            if (typeof isGuest === "undefined") {
                console.warn(
                    "API response does not contain isGuest:",
                    response.data
                );
            }

            commit("setUser", response.data);
            commit("setGuest", isGuest);
            commit("setError", null);

            // console.log("isGuest status (encrypted):", decrypt(localStorage.getItem("isGuest")));
            await dispatch("cart/fetchCart", null, { root: true });

            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred while fetching user data."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async resetPassword({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/resetPassword", payload);
            commit("setError", null);
            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred during password reset."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async changePassword({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.put("/user/change-password", {
                CurrentPassword: payload.currentPassword,
                NewPassword: payload.newPassword,
                ConfirmNewPassword: payload.confirmNewPassword,
            });
            commit("setError", null);
            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred during password change."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    // CH-W41 Manage Session
    async getSessions({ state }) {
        const response = await API_URL.get("/user/sessions", {
            headers: { Authorization: `Bearer ${state.token}` },
        });
        return response.data?.data || [];
    },
    async revokeSession({ state }, id) {
        return await API_URL.delete(`/user/sessions/${id}`, {
            headers: { Authorization: `Bearer ${state.token}` },
        });
    },
    async revokeOtherSessions({ state }, scope = "others") {
        return await API_URL.delete(`/user/sessions?scope=${scope}`, {
            headers: { Authorization: `Bearer ${state.token}` },
        });
    },

    async changePhoneNumber({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.put("/user/change-phone-number", {
                NewPhoneNumber: payload.NewPhoneNumber,
                Password: payload.Password,
                VerificationCode: payload.VerificationCode,
            });

            // Periksa apakah response memiliki 'code' dengan nilai 0
            if (response.data.code === 0) {
                commit("setError", null);
                return response; // Jangan melempar error jika status sukses
            } else {
                throw new Error(response.data.msg || "Unknown error occurred");
            }
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to change phone number."
            );
            throw error; // Hanya lempar error jika kondisi benar-benar gagal
        } finally {
            commit("setLoading", false);
        }
    },

    async changeEmail({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.put("/user/change-email", {
                NewEmail: payload.NewEmail,
                Password: payload.Password,
                VerificationCode: payload.VerificationCode,
            });

            if (response.data.code === 0) {
                commit("setError", null);
                return response;
            } else {
                throw new Error(response.data.msg || "Unknown error occurred");
            }
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to change email."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async updateProfile({ dispatch, commit, state }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.put("/user/profile", {
                Name: payload.Name ?? state.user?.Name ?? null,
                PhoneNumber: payload.PhoneNumber ?? state.user?.PhoneNumber ?? null,
                CountryId: payload.CountryId ?? state.user?.CountryId ?? null,
                ProvinceId:
                    payload.ProvinceId ?? state.user?.ProvinceId ?? null,
                CityId: payload.CityId ?? state.user?.CityId ?? null,
                IdentityType:
                    payload.IdentityType ?? state.user?.IdentityType ?? null,
                IdentityNumber:
                    payload.IdentityNumber ?? state.user?.IdentityNumber ?? null,
                IdentityImageId:
                    payload.IdentityImageId ?? state.user?.IdentityImageId ?? null,
                // CH-W38 — foto profil. Bukan data yang diverifikasi: menggantinya tidak mencabut badge.
                ProfileImageId:
                    payload.ProfileImageId ?? state.user?.ProfileImageId ?? null,
                EmergencyContactName:
                    payload.EmergencyContactName ??
                    state.user?.EmergencyContactName ??
                    null,
                EmergencyContactPhone:
                    payload.EmergencyContactPhone ??
                    state.user?.EmergencyContactPhone ??
                    null,
                EmergencyContactRelation:
                    payload.EmergencyContactRelation ??
                    state.user?.EmergencyContactRelation ??
                    null,
                EmergencyContactRelationOther:
                    payload.EmergencyContactRelationOther ??
                    state.user?.EmergencyContactRelationOther ??
                    null,
            });

            console.log("✅ Profile updated successfully:", response.data);

            // Perbarui state user dengan data terbaru dari respons
            await dispatch("fetchUser");

            return response.data;
        } catch (error) {
            console.error("❌ Error updating profile:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async getWebsiteSetting({ commit }) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get("/user/getWebsiteSetting");

            commit("setError", null);
            if (response.data.data) {
                const setting = response.data.data.find(
                    (e) => e.Code == [SETTINGS.WEBSITE_LOGO]
                );
                if (setting?.ImageOriginalName) {
                    commit("setWebsiteLogo", setting.ImageOriginalName);
                }
                if (setting && !setting.ImageOriginalName) {
                    commit("setWebsiteLogo", "");
                }
            }
            const monthlyBookingRequireLogin =
                response?.data?.meta?.Features?.MonthlyBookingRequireLogin ===
                true;
            commit(
                "setMonthlyBookingRequireLogin",
                monthlyBookingRequireLogin
            );

            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred while sending fingerprint."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async sendLogVisit({ commit }, body) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/sendLogVisit", body);
            commit("setError", null);
            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred while sending fingerprint."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async checkPhoneRegistered({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get(
                `/user/checkPhoneRegistered/${data}`
            );
            commit("setError", null);
            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred while sending fingerprint."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async checkWhatsappRegistered({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get(
                `/user/checkWhatsappRegistered/${data}`
            );
            commit("setError", null);
            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred while checking WhatsApp number."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async loginExclusive({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post("/user/login", payload);
            return response;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred during OTP sending."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    clearError({ commit }) {
        commit("clearError");
    },
};

const mutations = {
    setError(state, errorKey) {
        state.errorKey = errorKey; // Simpan key error
        state.errorMessage = errorKey ? i18n.global.t(errorKey) : null; // Terjemahkan errorKey
    },
    clearError(state) {
        state.error = null;
        state.errorMessage = null;
    },
    setLoading(state, loading) {
        state.loading = loading;
    },
    setUser(state, user) {
        if (user) {
            state.user = user.data;

            // Simpan user ke localStorage
            const encryptedUser = encrypt(JSON.stringify(user.data));
            localStorage.setItem("user", encryptedUser);

            // console.log("Encrypted user saved to localStorage:", encryptedUser);

            // Reset verification issues
            state.verificationIssues = [];
            if (!user.VerifiedEmail) {
                state.verificationIssues.push("email");
            }
            if (!user.VerifiedPhoneNumber) {
                state.verificationIssues.push("phone");
            }
        } else {
            state.user = null;
            state.verificationIssues = [];
            localStorage.removeItem("user");
        }
    },
    setToken(state, token) {
        state.token = token;
        if (token) {
            localStorage.setItem("token", token); // Simpan token di localStorage
            API_URL.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            return;
        }

        localStorage.removeItem("token");
        delete API_URL.defaults.headers.common["Authorization"];
    },
    setAuthenticated(state, status) {
        // Tambahkan mutation ini
        state.isAuthenticated = status;
    },
    setGuest(state, status) {
        // Tambahkan mutation ini
        if (typeof status !== "undefined" && status !== null) {
            state.isGuest = status;
            localStorage.setItem("isGuest", encrypt(status.toString())); // Simpan nilai terenkripsi
        } else {
            console.warn("Invalid or missing isGuest value. Skipping...");
        }
    },
    setRegistrationData(state, payload) {
        state.registrationData = { ...state.registrationData, ...payload };
    },
    resetRegistrationData(state) {
        state.registrationData = {
            name: "",
            emailOrPhoneNumber: "",
            password: "",
        };
    },
    setFingerprintId(state, fingerprintId) {
        state.fingerprintId = fingerprintId;
        localStorage.setItem("fingerprintId", fingerprintId);
    },
    setWebsiteLogo(state, websiteLogo) {
        state.websiteLogo = websiteLogo;
        if (websiteLogo == "") {
            localStorage.removeItem("WEBSITE_LOGO");
        } else {
            localStorage.setItem("WEBSITE_LOGO", websiteLogo);
        }
    },
    setMonthlyBookingRequireLogin(state, status) {
        state.monthlyBookingRequireLogin = status === true;
        localStorage.setItem(
            "MONTHLY_BOOKING_REQUIRE_LOGIN",
            state.monthlyBookingRequireLogin ? "true" : "false"
        );
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
